import AddCircle from '@mui/icons-material/AddCircle';
import Edit from '@mui/icons-material/Edit';
import { Typography } from '@mui/material';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import { Accordion } from '@volvo/vcdk-react/Accordion';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ServiceAgreementDto } from 'api';
import {
	agreementBusinessModelToTranslationMapping,
	agreementServiceTypeToTranslationMapping,
} from 'api/responses/models/Unit/ServiceAgreementDto';
import { AreaName } from 'domain/constants';
import { SectionButton } from 'library/components/controls/buttons/SectionButton';
import { NoDataAvailable } from 'library/components/controls/datagrid/NoDataAvailable';
import {
	InlineDisplayTextContainer,
	InlineDisplayValue,
} from 'library/components/controls/display/InlineDisplayValue';
import { YesNoDisplay } from 'library/components/controls/display/YesNoDisplay';
import { getLocalizedDateFormat } from 'library/formatters/DateFormatter';
import { formatValueWithSecondInBracket } from 'library/formatters/StringFormatter';
import { OperationType } from 'library/models/enums/OperationType';
import AuthorizeComponent from 'main/auth/AuthorizeComponent';
import { ChangedByInfoDisplay } from 'modules/UnitOverview/Components/ChangedByInfoDisplay';

import { ServiceAgreementLog } from './ServiceAgreementLog';

interface ServiceAgreementViewSectionProps {
	data: ServiceAgreementDto[];
	onEdit: (agreement: ServiceAgreementDto) => void;
	onAdd: () => void;
}

export const ServiceAgreementViewSection = ({
	data,
	onEdit,
	onAdd,
}: ServiceAgreementViewSectionProps): React.JSX.Element => {
	const { t } = useTranslation(['unitOverview', 'common', 'commonButton']);

	const activeServiceAgreement = data?.find(
		(agreement) => agreement.status === 'Active'
	);

	const [openIndex, setOpenIndex] = useState<number | undefined>(0);

	const onToggle = (index: number) => () => {
		setOpenIndex((prevIndex) => (prevIndex === index ? undefined : index));
	};

	return (
		<>
			<AuthorizeComponent
				areaName={AreaName.UnitConfiguration_EditServiceAgreement}
				permission={OperationType.Write}>
				<Box
					sx={{
						display: 'flex',
						justifyContent: 'flex-end',
						gap: 1,
					}}>
					<SectionButton
						text={t('commonButton:add')}
						icon={<AddCircle />}
						onClick={(e) => {
							e.stopPropagation();
							onAdd();
						}}
					/>
					{activeServiceAgreement && (
						<SectionButton
							text={t('commonButton:edit')}
							icon={<Edit />}
							onClick={(e) => {
								e.stopPropagation();
								onEdit(activeServiceAgreement);
							}}
						/>
					)}
				</Box>
			</AuthorizeComponent>
			<Accordion open={openIndex === 0} onToggle={onToggle(0)}>
				<span slot="title">
					{t('unitOverview:service-agreement-active-header')}
				</span>

				{activeServiceAgreement ? (
					<Paper
						variant="bottomBorder"
						sx={{
							p: { xs: 0, sm: 2 },
						}}>
						<InlineDisplayTextContainer
							columnsCount={2}
							rowsCount={8}>
							<InlineDisplayValue
								label={t(
									'unitOverview:service-agreement-agreement-period'
								)}
								value={`${getLocalizedDateFormat(activeServiceAgreement.startDate)} - ${getLocalizedDateFormat(activeServiceAgreement.endDate)}`}
							/>
							<InlineDisplayValue
								label={t(
									'unitOverview:service-agreement-start-hour'
								)}
								value={activeServiceAgreement.startHour}
							/>
							<InlineDisplayValue
								label={t(
									'unitOverview:service-agreement-max-hour'
								)}
								value={activeServiceAgreement.maxHour}
							/>
							<InlineDisplayValue
								label={t(
									'unitOverview:service-agreement-service-type'
								)}
								value={
									activeServiceAgreement.serviceType
										? t(
												agreementServiceTypeToTranslationMapping[
													activeServiceAgreement
														.serviceType
												]
											)
										: ''
								}
							/>
							<InlineDisplayValue
								label={t(
									'unitOverview:service-agreement-business-model'
								)}
								value={
									activeServiceAgreement.businessModel
										? t(
												agreementBusinessModelToTranslationMapping[
													activeServiceAgreement
														.businessModel
												]
											)
										: ''
								}
							/>
							<InlineDisplayValue
								label={t(
									'unitOverview:service-agreement-reference'
								)}
								value={activeServiceAgreement.reference}
							/>
							<InlineDisplayValue
								label={t(
									'unitOverview:responsible-business-partner-label'
								)}
								value={formatValueWithSecondInBracket(
									activeServiceAgreement.businessPartnerName,
									activeServiceAgreement.businessPartnerId
								)}
							/>
							<InlineDisplayValue
								label={t(
									'unitOverview:service-agreement-additional-information'
								)}
								value={activeServiceAgreement.additionalContent}
							/>
							<InlineDisplayValue
								label={t(
									'unitOverview:service-agreement-preventive-maintenance'
								)}
								value={
									<YesNoDisplay
										value={
											activeServiceAgreement.preventiveMaintenance
										}
									/>
								}
							/>
							<InlineDisplayValue
								label={t(
									'unitOverview:service-agreement-genuine-oil'
								)}
								value={
									<YesNoDisplay
										value={
											activeServiceAgreement.genuineOil
										}
									/>
								}
							/>
							<InlineDisplayValue
								label={t(
									'unitOverview:service-agreement-preventive-repair'
								)}
								value={
									<YesNoDisplay
										value={
											activeServiceAgreement.preventiveRepair
										}
									/>
								}
							/>
							<InlineDisplayValue
								label={t(
									'unitOverview:service-agreement-service-calculator'
								)}
								value={
									<YesNoDisplay
										value={activeServiceAgreement.tcoUsed}
									/>
								}
							/>
							<AuthorizeComponent
								areaName={
									AreaName.UnitConfiguration_ServiceAgreementAdmin
								}
								permission={OperationType.Write}>
								<InlineDisplayValue
									label={t(
										'unitOverview:service-agreement-validated-by-volvo-penta'
									)}
									value={
										<YesNoDisplay
											value={
												activeServiceAgreement.validatedByVolvoPenta
											}
										/>
									}
								/>
							</AuthorizeComponent>
						</InlineDisplayTextContainer>
						<Box
							sx={{
								display: 'flex',
								justifyContent: 'flex-end',
							}}>
							<ChangedByInfoDisplay
								changedByInfo={
									activeServiceAgreement.changedByInfo
										.changedByUserId != null
										? activeServiceAgreement.changedByInfo
										: activeServiceAgreement.createdByInfo
								}
							/>
						</Box>
					</Paper>
				) : (
					<Typography variant="body2" color="text.secondary">
						{t('unitOverview:service-agreement-no-active')}
					</Typography>
				)}
			</Accordion>
			<Accordion open={openIndex === 1} onToggle={onToggle(1)}>
				<span slot="title">
					{t('unitOverview:service-agreement-history')}
				</span>
				{data.length > 0 ? (
					<ServiceAgreementLog data={data} onEdit={onEdit} />
				) : (
					<NoDataAvailable />
				)}
			</Accordion>
		</>
	);
};
